import { Injectable } from '@angular/core';
import {
  Action,
  Selector,
  State,
  StateContext,
  createSelector,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  ShiftTimelineFilterUnassignedRegionStateModel,
  ShiftTimelineUnassignedRegionStateModel,
} from '../interfaces';
import { ShiftTimelineUnassignedRegionState } from '../shift-timeline-unassigned-region/shift-timeline-unassigned-region.state';
import {
  SetUnassignedServiceIdsFilter,
  SetUnassignedServiceSearchesFilter,
  SetUnassignedShiftIdsFilter,
  SetUnassignedShiftSearchesFilter,
} from './shift-timeline-filter-unassigned-region.actions';
import {
  Activity,
  GroupedUnassignedUserDataV2,
  UnassignedShiftStackByDay,
} from '@wilson/interfaces';
import {
  determineUnassignmentsOverlaps,
  groupActivitiesByServiceAndJob,
} from '@wilson/non-domain-specific/overlap-helpers';
import { isDebugEnabled } from '@wilson/non-domain-specific/logger/logger';

const defaults: ShiftTimelineFilterUnassignedRegionStateModel = {
  unassignedShiftSearches: [],
  unassignedShiftIds: [],
  unassignedServiceSearches: [],
  unassignedServiceIds: [],
};

@State<ShiftTimelineFilterUnassignedRegionStateModel>({
  name: 'shiftTimelineFilterUnassignedRegion',
  defaults,
})
@Injectable()
export class ShiftTimelineFilterUnassignedRegionState {
  static filteredUnassignedActivities(): (
    unassignedActivities: ShiftTimelineUnassignedRegionStateModel['unassignedActivities'],
    filterRegionState: ShiftTimelineFilterUnassignedRegionStateModel,
  ) => GroupedUnassignedUserDataV2[] {
    return createSelector(
      [
        ShiftTimelineUnassignedRegionState.unassignedActivities,
        ShiftTimelineFilterUnassignedRegionState,
      ],
      (
        unassignedActivities: ShiftTimelineUnassignedRegionStateModel['unassignedActivities'],
        filterRegionState: ShiftTimelineFilterUnassignedRegionStateModel,
      ) => {
        const { unassignedServiceIds, unassignedServiceSearches } =
          filterRegionState;

        const filteredActivities = this.filterUnassignedActivities(
          unassignedActivities,
          unassignedServiceIds,
          unassignedServiceSearches,
        );

        return determineUnassignmentsOverlaps(
          Object.assign(groupActivitiesByServiceAndJob(filteredActivities), {
            isDebugEnabled: isDebugEnabled(),
          }),
        );
      },
    );
  }

  @Selector()
  static unassignedShiftIds(
    state: ShiftTimelineFilterUnassignedRegionStateModel,
  ): string[] {
    return state.unassignedShiftIds;
  }

  @Selector()
  static unassignedShiftSearches(
    state: ShiftTimelineFilterUnassignedRegionStateModel,
  ): string[] {
    return state.unassignedShiftSearches;
  }

  @Action(SetUnassignedShiftIdsFilter)
  setUnassignedShiftIdsFilter(
    ctx: StateContext<ShiftTimelineFilterUnassignedRegionStateModel>,
    action: SetUnassignedShiftIdsFilter,
  ): void {
    ctx.setState(
      patch<ShiftTimelineFilterUnassignedRegionStateModel>({
        unassignedShiftIds: action.ids,
      }),
    );
  }

  @Action(SetUnassignedShiftSearchesFilter)
  setFilters(
    ctx: StateContext<ShiftTimelineFilterUnassignedRegionStateModel>,
    action: SetUnassignedShiftSearchesFilter,
  ): void {
    ctx.setState(
      patch<ShiftTimelineFilterUnassignedRegionStateModel>({
        unassignedShiftSearches: action.searchStrings,
      }),
    );
  }

  @Action(SetUnassignedServiceIdsFilter)
  setUnassignedServiceIdsFilter(
    ctx: StateContext<ShiftTimelineFilterUnassignedRegionStateModel>,
    action: SetUnassignedServiceIdsFilter,
  ): void {
    ctx.setState(
      patch<ShiftTimelineFilterUnassignedRegionStateModel>({
        unassignedServiceIds: action.ids,
      }),
    );
  }

  @Action(SetUnassignedServiceSearchesFilter)
  setActivityFilters(
    ctx: StateContext<ShiftTimelineFilterUnassignedRegionStateModel>,
    action: SetUnassignedServiceSearchesFilter,
  ): void {
    ctx.setState(
      patch({
        unassignedServiceSearches: action.searchStrings,
      }),
    );
  }

  static filterUnassignedActivities(
    activities: Activity[],
    unassignedServiceIds: string[],
    unassignedServiceSearches: string[],
  ): Activity[] {
    let filteredActivities: Activity[] =
      !unassignedServiceIds.length && !unassignedServiceSearches.length
        ? activities
        : [];

    if (unassignedServiceIds.length) {
      filteredActivities = activities.filter(
        (activity) =>
          activity.service &&
          unassignedServiceIds.includes(activity.service.id),
      );
    }

    if (unassignedServiceSearches.length) {
      const remainingActivities = activities.filter(
        (activity) =>
          activity.service &&
          !unassignedServiceIds.includes(activity.service.id),
      );
      const filteredBySearch = remainingActivities.filter((activity) => {
        return unassignedServiceSearches.some((searchString) =>
          activity.service?.name
            .toLocaleLowerCase()
            .includes(searchString.toLocaleLowerCase()),
        );
      });
      filteredActivities = [...filteredActivities, ...filteredBySearch];
    }

    return filteredActivities;
  }

  static filterUnassignedShiftStacks(
    unassignedShiftStacks: UnassignedShiftStackByDay[],
    unassignedShiftIds: string[],
    unassignedShiftSearches: string[],
  ): UnassignedShiftStackByDay[] {
    const isNoFilterAtAll =
      !unassignedShiftIds.length && !unassignedShiftSearches.length;
    const isOnlyShiftIdsFilter =
      unassignedShiftIds.length && !unassignedShiftSearches.length;
    const isOnlyShiftSearchesFilter =
      !unassignedShiftIds.length && unassignedShiftSearches.length;
    const areBothFilters =
      unassignedShiftIds.length && unassignedShiftSearches.length;

    const clonedStack = () => {
      return unassignedShiftStacks.map((stack) => {
        return {
          day: stack.day,
          shifts: { ...stack.shifts },
        };
      });
    };

    const lowerCasedNamesFunc = () =>
      unassignedShiftSearches.map((name) => name.toLocaleLowerCase());

    if (isNoFilterAtAll) {
      return unassignedShiftStacks;
    } else if (isOnlyShiftIdsFilter) {
      return clonedStack().map((stack) => {
        Object.entries(stack.shifts).forEach(([shiftId]) => {
          if (
            unassignedShiftIds.length &&
            !unassignedShiftIds.includes(shiftId)
          ) {
            delete stack.shifts[shiftId];
          }
        });

        return stack;
      });
    } else if (isOnlyShiftSearchesFilter) {
      const clonsedUnassignedShiftStacks = clonedStack();
      const lowerCasedNames = lowerCasedNamesFunc();

      return clonsedUnassignedShiftStacks.map((stack) => {
        Object.entries(stack.shifts).forEach(([shiftId, shift]) => {
          const isMatchingName = lowerCasedNames.some(
            (lowerCasedSearchString) =>
              shift.name.toLocaleLowerCase().includes(lowerCasedSearchString),
          );

          if (!isMatchingName) {
            delete stack.shifts[shiftId];
          }
        });

        return stack;
      });
    } else if (areBothFilters) {
      const clonsedUnassignedShiftStacks = clonedStack();
      const lowerCasedNames = lowerCasedNamesFunc();

      return clonsedUnassignedShiftStacks.map((stack) => {
        Object.entries(stack.shifts).forEach(([shiftId, shift]) => {
          const isMatchingId = unassignedShiftIds.includes(shiftId);
          const isMatchingName = lowerCasedNames.some(
            (lowerCasedSearchString) =>
              shift.name.toLocaleLowerCase().includes(lowerCasedSearchString),
          );
          if (!isMatchingId && !isMatchingName) {
            delete stack.shifts[shiftId];
          }
        });

        return stack;
      });
    } else {
      return unassignedShiftStacks;
    }
  }

  static filterUnassignedShift<T>(
    shifts: (T & { id: string; name: string })[],
    unassignedShiftIdsFilter: string[],
    unassignedShiftSearches: string[],
  ): (T & { id: string; name: string })[] {
    let filteredShifts: (T & { id: string; name: string })[] =
      !unassignedShiftIdsFilter.length && !unassignedShiftSearches.length
        ? shifts
        : [];
    if (unassignedShiftIdsFilter.length) {
      filteredShifts = this.filterByIds(shifts, unassignedShiftIdsFilter);
    }

    if (unassignedShiftSearches.length) {
      const remainingShifts = shifts.filter(
        (shift) => !unassignedShiftIdsFilter.includes(shift.id),
      );

      filteredShifts = [
        ...filteredShifts,
        ...this.filterBySearches(remainingShifts, unassignedShiftSearches),
      ];
    }

    return filteredShifts;
  }

  static filterByIds<T>(
    shifts: (T & { id: string })[],
    ids: string[],
  ): (T & { id: string })[] {
    return shifts.filter((shift) => ids.includes(shift.id));
  }

  static filterBySearches<T>(
    shifts: (T & { name: string })[],
    searches: string[],
  ): (T & { name: string })[] {
    return shifts.filter((shift) =>
      searches.some((searchString) =>
        shift.name
          .toLocaleLowerCase()
          .includes(searchString.toLocaleLowerCase()),
      ),
    );
  }
}
